import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { motion, useScroll, useTransform } from 'framer-motion';
import { Footer, Navbar } from '../../components/Layout/Layout';
import { AddressAutocomplete } from '../../components/AddressAutocomplete/AddressAutocomplete';
import { AnimatedCounter } from '../../components/Hero/AnimatedCounter';
import { LiveDirectoryMap } from '../../components/Hero/LiveDirectoryMap';
import { Magnetic } from '../../components/Hero/Magnetic';
import { AuroraBackground } from '../../components/Aurora/AuroraBackground';
import { TrustScoreRadar } from '../../components/Trust/TrustScoreRadar';
import api from '../../lib/api';
import { toast } from 'sonner';
import { Search, MapPin, Shield, Zap, ArrowRight, FileCheck, Award, Route, ArrowUpRight } from 'lucide-react';
import useCanonical from '../../hooks/useCanonical';

const STATE_NAMES_MAP = {
  AL: 'Alabama', AK: 'Alaska', AZ: 'Arizona', AR: 'Arkansas', CA: 'California',
  CO: 'Colorado', CT: 'Connecticut', DE: 'Delaware', FL: 'Florida', GA: 'Georgia',
  HI: 'Hawaii', ID: 'Idaho', IL: 'Illinois', IN: 'Indiana', IA: 'Iowa',
  KS: 'Kansas', KY: 'Kentucky', LA: 'Louisiana', ME: 'Maine', MD: 'Maryland',
  MA: 'Massachusetts', MI: 'Michigan', MN: 'Minnesota', MS: 'Mississippi', MO: 'Missouri',
  MT: 'Montana', NE: 'Nebraska', NV: 'Nevada', NH: 'New Hampshire', NJ: 'New Jersey',
  NM: 'New Mexico', NY: 'New York', NC: 'North Carolina', ND: 'North Dakota', OH: 'Ohio',
  OK: 'Oklahoma', OR: 'Oregon', PA: 'Pennsylvania', RI: 'Rhode Island', SC: 'South Carolina',
  SD: 'South Dakota', TN: 'Tennessee', TX: 'Texas', UT: 'Utah', VT: 'Vermont',
  VA: 'Virginia', WA: 'Washington', WV: 'West Virginia', WI: 'Wisconsin', WY: 'Wyoming',
  DC: 'Washington D.C.',
};

const FEATURES = [
  { icon: Shield,    title: 'Servio Score™',       blurb: 'Weighted 5-dimensional trust rating. No pay-to-rank. Every server measured on the same axes.', tag: 'TRUST' },
  { icon: MapPin,    title: 'Coverage that maps',  blurb: 'States, counties, cities, ZIPs. Set fixed rates, ranges, or "call for quote" per region.', tag: 'GEO' },
  { icon: Zap,       title: 'Instant quotes',      blurb: 'Clients see prices upfront. Rush and same-day surcharges built in. Accept in one tap.', tag: 'SPEED' },
  { icon: Route,     title: 'Route optimizer',     blurb: 'Import stops from CSV. Drag to reorder. Time-window aware. Native maps app handoff.', tag: 'OPS' },
  { icon: FileCheck, title: 'Invoice & payments',  blurb: 'Stripe-native invoices, statements, branded portal, online card payments. No platform fees.', tag: 'BILLING' },
  { icon: Award,     title: 'Verified credentials',blurb: 'Upload licenses, bonds, and insurance — earn badges that show up everywhere your profile does.', tag: 'PROOF' },
];

function HomePage() {
  const navigate = useNavigate();
  const [selectedLocation, setSelectedLocation] = useState(null);
  const [foundingData, setFoundingData] = useState({ remaining: 100 });
  const [stats, setStats] = useState(null);

  useCanonical('https://servio.legal/');

  useEffect(() => {
    api.getFoundingCount().then((r) => setFoundingData(r.data)).catch(() => {});
    api.getDirectoryStats().then((r) => setStats(r.data)).catch(() => {});
  }, []);

  const { scrollYProgress } = useScroll();
  const heroParallax = useTransform(scrollYProgress, [0, 0.2], [0, -60]);

  function handleLocationSelect(loc) { setSelectedLocation(loc); }

  function handleSearch(e) {
    e.preventDefault();
    if (!selectedLocation) { toast.error('Please select a location to search'); return; }
    const params = new URLSearchParams();
    if (selectedLocation.coordinates) {
      params.set('lng', selectedLocation.coordinates[0]);
      params.set('lat', selectedLocation.coordinates[1]);
    }
    params.set('place_name', selectedLocation.name);
    params.set('place_type', selectedLocation.placeType);
    if (selectedLocation.placeType === 'state' && selectedLocation.stateCode) params.set('state', selectedLocation.stateCode);
    if (selectedLocation.placeType === 'county') {
      if (selectedLocation.county) params.set('county', selectedLocation.county);
      if (selectedLocation.stateCode) params.set('state', selectedLocation.stateCode);
    }
    navigate('/search?' + params.toString());
  }

  const serifStyle = { fontFamily: '"Instrument Serif"' };

  return (
    <div className="min-h-screen flex flex-col bg-white dark:bg-[hsl(220,40%,5%)] text-slate-900 dark:text-[hsl(220,10%,96%)]">
      <Helmet>
        <title>Servio · Modern directory for process servers</title>
        <meta name="description" content="Servio is the modern directory for legal process servers — Servio Score™ trust ratings, instant quotes, real-time coverage map, nationwide reach." />
        <link rel="canonical" href="https://servio.legal/" />
      </Helmet>

      <Navbar />

      {/* ════════════════  HERO  ════════════════ */}
      <section className="relative overflow-hidden isolate" data-testid="hero-section">
        <div className="hidden dark:block"><AuroraBackground intensity={0.95} /></div>
        {/* Light mode: soft cobalt mesh */}
        <div className="absolute inset-0 dark:hidden pointer-events-none" style={{
          background: 'radial-gradient(800px 400px at 20% 0%, hsl(226 100% 55% / 0.08), transparent 60%), radial-gradient(900px 500px at 95% 10%, hsl(270 95% 60% / 0.06), transparent 60%), radial-gradient(700px 500px at 50% 100%, hsl(150 80% 55% / 0.05), transparent 60%)',
        }} />
        {/* Subtle grid for light mode */}
        <div className="absolute inset-0 dark:hidden opacity-[0.5] pointer-events-none" style={{
          backgroundImage: 'linear-gradient(hsl(220 16% 92%) 1px, transparent 1px), linear-gradient(90deg, hsl(220 16% 92%) 1px, transparent 1px)',
          backgroundSize: '48px 48px',
          maskImage: 'radial-gradient(ellipse 80% 60% at 50% 30%, black, transparent 75%)',
          WebkitMaskImage: 'radial-gradient(ellipse 80% 60% at 50% 30%, black, transparent 75%)',
        }} />

        <motion.div style={{ y: heroParallax }} className="relative z-10 max-w-[1280px] mx-auto px-5 sm:px-8 pt-16 pb-20 lg:pt-20 lg:pb-24">
          <motion.div initial={{ opacity: 0, y: -8 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }} className="flex items-center justify-center gap-2 mb-10">
            <span className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full bg-white/[0.06] backdrop-blur-md border border-white/10 text-xs font-medium" data-testid="launch-banner">
              <span className="relative flex h-1.5 w-1.5">
                <span className="absolute inline-flex h-full w-full rounded-full bg-[hsl(150,80%,60%)] opacity-70 animate-ping" />
                <span className="relative inline-flex h-1.5 w-1.5 rounded-full bg-[hsl(150,80%,60%)]" />
              </span>
              <span className="text-[hsl(220,12%,80%)]">Founding pricing live ·</span>
              <span className="font-mono tabular-nums text-[hsl(150,80%,60%)]">
                <AnimatedCounter to={foundingData.remaining} duration={1.4} />
              </span>
              <span className="text-[hsl(220,12%,80%)]">seats left</span>
            </span>
          </motion.div>

          <motion.h1
            initial={{ opacity: 0, y: 14 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
            className="text-center font-display font-bold leading-[0.95] tracking-[-0.045em] mx-auto max-w-5xl"
            style={{ fontSize: 'clamp(2.2rem, 5.6vw, 4.6rem)' }}
          >
            <span className="block">The directory</span>
            <span className="block">
              <span>legal </span>
              <span className="italic font-normal text-[hsl(226,100%,52%)] dark:text-[hsl(226,100%,68%)] dark:glow-cobalt" style={serifStyle}>actually</span>
              <span> trusts.</span>
            </span>
          </motion.h1>

          <motion.p initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, delay: 0.3 }} className="mt-6 max-w-xl mx-auto text-center text-base sm:text-lg leading-relaxed text-slate-600 dark:text-[hsl(220,12%,72%)]">
            Verified process servers, a five-dimensional trust score, instant quotes, and a live coverage map of all fifty states. Free to search. Free to list.
          </motion.p>

          <motion.form onSubmit={handleSearch} initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, delay: 0.5 }} className="mt-8 max-w-2xl mx-auto" data-testid="search-form">
            <div className="group relative rounded-2xl bg-white dark:bg-[hsl(220,30%,8%)] backdrop-blur-xl border border-slate-200 dark:border-white/10 hover:border-slate-300 dark:hover:border-white/20 transition-all p-2 flex items-center gap-2 shadow-[0_8px_24px_-8px_rgba(15,23,42,0.10)] dark:shadow-[0_24px_60px_-16px_rgba(30,91,246,0.45)]">
              <div className="flex-1 min-w-0">
                <AddressAutocomplete onSelect={handleLocationSelect} placeholder="Search any address, city, county, ZIP, or state…" />
              </div>
              <Magnetic strength={10}>
                <button type="submit" className="h-12 px-6 rounded-xl bg-[hsl(226,100%,55%)] hover:bg-[hsl(226,100%,62%)] text-white font-semibold tracking-tight flex items-center gap-2 whitespace-nowrap shadow-[0_10px_30px_-8px_hsl(226,100%,55%)] transition-colors" data-testid="search-btn">
                  <Search className="h-4 w-4" />
                  Search
                </button>
              </Magnetic>
            </div>
            <div className="mt-4 flex flex-wrap items-center justify-center gap-x-5 gap-y-2 text-[11px] font-mono uppercase tracking-[0.18em] text-slate-400 dark:text-[hsl(220,12%,55%)]">
              <span>FREE</span><span>·</span><span>VERIFIED</span><span>·</span><span>INSTANT QUOTES</span>
            </div>
          </motion.form>

          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8, delay: 0.7 }} className="mt-16 grid grid-cols-2 lg:grid-cols-4 gap-3 max-w-5xl mx-auto">
            {[
              { value: stats?.total_servers ?? 0, label: 'Servers Listed',    color: 'hsl(226,100%,55%)' },
              { value: stats?.states_covered ?? 0, label: 'States Covered',    color: 'hsl(150,80%,40%)' },
              { value: stats?.avg_score ?? 0,      label: 'Avg Servio Score',  color: 'hsl(38,95%,50%)'  },
              { value: stats?.founding?.remaining ?? foundingData.remaining, label: 'Founding Seats Left', color: 'hsl(320,90%,55%)' },
            ].map((s, i) => (
              <div key={i} className="relative rounded-2xl border border-slate-200 dark:border-white/10 bg-white dark:bg-[hsl(220,30%,8%)] backdrop-blur-sm p-5 hover:bg-slate-50 dark:hover:bg-[hsl(220,30%,10%)] transition-colors shadow-soft dark:shadow-none">
                <div className="absolute top-3 right-3 h-1.5 w-1.5 rounded-full" style={{ background: s.color, boxShadow: `0 0 12px ${s.color}` }} />
                <div className="font-mono font-bold tabular-nums tracking-tight dark:[color:var(--c)]" style={{ fontSize: 'clamp(1.6rem, 3vw, 2.4rem)', color: s.color, '--c': s.color }}>
                  <AnimatedCounter to={s.value} duration={1.8} />
                </div>
                <div className="mt-1 text-[11px] font-mono uppercase tracking-[0.18em] text-slate-500 dark:text-[hsl(220,12%,55%)]">{s.label}</div>
              </div>
            ))}
          </motion.div>
        </motion.div>
      </section>

      {/* ════════════════  LIVE COVERAGE  ════════════════ */}
      <section className="relative border-t border-slate-200 dark:border-white/[0.06] py-20 lg:py-24" data-testid="coverage-section">
        <div className="absolute inset-0 pointer-events-none hidden dark:block" style={{ background: 'radial-gradient(60% 50% at 50% 0%, hsl(226 100% 55% / 0.12), transparent 70%)' }} />
        <div className="relative max-w-[1280px] mx-auto px-5 sm:px-8">
          <div className="flex items-end justify-between flex-wrap gap-6 mb-10">
            <div>
              <div className="font-mono text-[11px] tracking-[0.22em] uppercase text-[hsl(226,100%,52%)] dark:text-[hsl(226,100%,68%)] mb-3">◢ LIVE COVERAGE</div>
              <h2 className="font-display font-bold tracking-[-0.035em] leading-[0.95]" style={{ fontSize: 'clamp(1.8rem, 3.4vw, 2.8rem)' }}>
                {stats?.states_covered ?? '—'} states. <span className="italic font-normal text-slate-500 dark:text-[hsl(220,12%,60%)]" style={serifStyle}>Every server pinned.</span>
              </h2>
            </div>
            <p className="max-w-md text-sm text-slate-600 dark:text-[hsl(220,12%,70%)] leading-relaxed">Each dot is a verified server placed at the address they registered. Live pins pulse green. Hover to see the city, state, and Servio Score.</p>
          </div>

          <motion.div initial={{ opacity: 0, scale: 0.97 }} whileInView={{ opacity: 1, scale: 1 }} viewport={{ once: true, margin: '0px 0px -100px 0px' }} transition={{ duration: 0.9 }} className="relative rounded-3xl border border-slate-200 dark:border-white/10 bg-white dark:bg-[hsl(220,30%,8%)] backdrop-blur-sm p-5 lg:p-8 overflow-hidden shadow-soft dark:shadow-none">
            <div className="mx-auto max-w-4xl">
              <LiveDirectoryMap onStateClick={(s) => navigate(`/search?place_name=${encodeURIComponent(s.name)}&place_type=state&state=${s.code}`)} />
            </div>
            <div className="mt-5 grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
              {(stats?.state_counts || []).slice(0, 4).map(([code, count], i) => (
                <div key={code} className="rounded-xl border border-slate-200 dark:border-white/10 bg-slate-50 dark:bg-[hsl(220,30%,10%)] px-4 py-3">
                  <div className="font-semibold tracking-tight text-sm">{STATE_NAMES_MAP[code] || code}</div>
                  <div className="font-mono text-xs text-slate-500 dark:text-[hsl(220,12%,60%)] mt-0.5">{count} {count === 1 ? 'server' : 'servers'}</div>
                  <div className="font-mono text-[10px] uppercase tracking-[0.18em] text-[hsl(226,100%,52%)] dark:text-[hsl(226,100%,68%)] mt-1">#{i + 1} by coverage</div>
                </div>
              ))}
            </div>
          </motion.div>
        </div>
      </section>

      {/* ════════════════  TRUST SCORE  ════════════════ */}
      <section className="relative border-t border-slate-200 dark:border-white/[0.06] py-20 lg:py-24" data-testid="trust-section">
        <div className="max-w-[1280px] mx-auto px-5 sm:px-8">
          <div className="flex items-end justify-between flex-wrap gap-6 mb-10">
            <div>
              <div className="font-mono text-[11px] tracking-[0.22em] uppercase text-[hsl(150,80%,40%)] dark:text-[hsl(150,80%,60%)] mb-3">◢ THE SCORE</div>
              <h2 className="font-display font-bold tracking-[-0.035em] leading-[0.95] max-w-3xl" style={{ fontSize: 'clamp(1.8rem, 3.4vw, 2.8rem)' }}>
                Five numbers <span className="italic font-normal text-slate-500 dark:text-[hsl(220,12%,60%)]" style={serifStyle}>that decide</span> who gets the job.
              </h2>
            </div>
            <p className="max-w-md text-sm text-slate-600 dark:text-[hsl(220,12%,70%)] leading-relaxed">The Servio Score™ is a weighted composite — not a popularity contest. Hover an axis to see how it breaks down.</p>
          </div>

          <motion.div initial={{ opacity: 0, y: 24 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, margin: '0px 0px -100px 0px' }} transition={{ duration: 0.8 }} className="rounded-3xl border border-slate-200 dark:border-white/10 bg-white dark:bg-[hsl(220,30%,8%)] backdrop-blur-sm p-6 lg:p-10 shadow-soft dark:shadow-none">
            <TrustScoreRadar />
          </motion.div>
        </div>
      </section>

      {/* ════════════════  FEATURES  ════════════════ */}
      <section className="relative border-t border-slate-200 dark:border-white/[0.06] py-20 lg:py-24" data-testid="features-section">
        <div className="max-w-[1280px] mx-auto px-5 sm:px-8">
          <div className="mb-10 max-w-3xl">
            <div className="font-mono text-[11px] tracking-[0.22em] uppercase text-[hsl(38,95%,45%)] dark:text-[hsl(38,95%,60%)] mb-3">◢ TOOLKIT</div>
            <h2 className="font-display font-bold tracking-[-0.035em] leading-[0.95]" style={{ fontSize: 'clamp(1.8rem, 3.4vw, 2.8rem)' }}>
              Built for the way <span className="italic font-normal text-slate-500 dark:text-[hsl(220,12%,60%)]" style={serifStyle}>legal work</span> actually moves.
            </h2>
          </div>
          <motion.div initial="hidden" whileInView="show" viewport={{ once: true, margin: '0px 0px -100px 0px' }} variants={{ hidden: {}, show: { transition: { staggerChildren: 0.08 } } }} className="grid md:grid-cols-2 lg:grid-cols-3 gap-3">
            {FEATURES.map((f, i) => (
              <motion.div key={i} variants={{ hidden: { opacity: 0, y: 20 }, show: { opacity: 1, y: 0, transition: { duration: 0.6 } } }} className="group relative rounded-2xl border border-slate-200 dark:border-white/10 bg-white dark:bg-[hsl(220,30%,8%)] p-6 overflow-hidden hover:border-[hsl(226,100%,55%)]/40 transition-colors shadow-soft dark:shadow-none" data-testid={`feature-card-${i}`}>
                <div className="absolute -top-24 -right-24 w-48 h-48 rounded-full bg-[hsl(226,100%,55%)]/0 group-hover:bg-[hsl(226,100%,55%)]/15 blur-3xl transition-colors" />
                <div className="relative flex items-start justify-between mb-5">
                  <div className="h-10 w-10 rounded-xl bg-slate-100 dark:bg-white/5 border border-slate-200 dark:border-white/10 flex items-center justify-center">
                    <f.icon className="h-4.5 w-4.5 text-[hsl(226,100%,52%)] dark:text-[hsl(226,100%,68%)]" strokeWidth={2} />
                  </div>
                  <span className="font-mono text-[10px] tracking-[0.22em] text-slate-400 dark:text-[hsl(220,12%,45%)]">{f.tag}</span>
                </div>
                <h3 className="text-base font-semibold tracking-tight mb-2">{f.title}</h3>
                <p className="text-sm leading-relaxed text-slate-600 dark:text-[hsl(220,12%,70%)]">{f.blurb}</p>
                <ArrowUpRight className="absolute bottom-5 right-5 h-4 w-4 text-slate-300 dark:text-[hsl(220,12%,40%)] group-hover:text-[hsl(226,100%,52%)] dark:group-hover:text-[hsl(226,100%,68%)] group-hover:-translate-y-1 group-hover:translate-x-1 transition-all" />
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* ════════════════  STATE INDEX  ════════════════ */}
      <section className="relative border-t border-slate-200 dark:border-white/[0.06] py-20 lg:py-24" data-testid="state-index-section">
        <div className="max-w-[1280px] mx-auto px-5 sm:px-8">
          <div className="flex items-end justify-between flex-wrap gap-6 mb-10">
            <div>
              <div className="font-mono text-[11px] tracking-[0.22em] uppercase text-[hsl(320,90%,52%)] dark:text-[hsl(320,90%,65%)] mb-3">◢ STATE INDEX</div>
              <h2 className="font-display font-bold tracking-[-0.035em] leading-[0.95]" style={{ fontSize: 'clamp(1.8rem, 3.4vw, 2.8rem)' }}>
                Every state. <span className="italic font-normal text-slate-500 dark:text-[hsl(220,12%,60%)]" style={serifStyle}>One tap away.</span>
              </h2>
            </div>
            <p className="max-w-md text-sm text-slate-600 dark:text-[hsl(220,12%,70%)] leading-relaxed">Browse the directory by jurisdiction. Numbers are live counts of verified servers presently listed.</p>
          </div>

          <motion.div initial="hidden" whileInView="show" viewport={{ once: true, margin: '0px 0px -80px 0px' }} variants={{ hidden: {}, show: { transition: { staggerChildren: 0.012 } } }} className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-2">
            {[...(stats?.state_counts || [])].sort((a, b) => (STATE_NAMES_MAP[a[0]] || a[0]).localeCompare(STATE_NAMES_MAP[b[0]] || b[0])).map(([code, count]) => (
              <motion.div key={code} variants={{ hidden: { opacity: 0, y: 8 }, show: { opacity: 1, y: 0, transition: { duration: 0.35 } } }}>
                <Link to={`/search?place_name=${encodeURIComponent(STATE_NAMES_MAP[code] || code)}&place_type=state&state=${code}`} className="group block rounded-xl border border-slate-200 dark:border-white/10 bg-white dark:bg-[hsl(220,30%,8%)] hover:bg-slate-50 dark:hover:bg-[hsl(220,30%,11%)] hover:border-[hsl(226,100%,55%)]/40 transition-all p-3.5 relative overflow-hidden shadow-soft-sm dark:shadow-none" data-testid={`state-row-${code}`}>
                  <div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity" style={{ background: 'radial-gradient(60% 80% at 50% 100%, hsl(226 100% 55% / 0.18), transparent 70%)' }} />
                  <div className="relative flex items-start justify-between">
                    <div>
                      <div className="font-mono text-[10px] tracking-[0.22em] text-slate-400 dark:text-[hsl(220,12%,45%)] group-hover:text-[hsl(226,100%,52%)] dark:group-hover:text-[hsl(226,100%,68%)] transition-colors">{code}</div>
                      <div className="font-semibold tracking-tight mt-1 leading-tight text-sm">{STATE_NAMES_MAP[code] || code}</div>
                    </div>
                    <div className="font-mono text-xs font-bold tabular-nums text-slate-500 dark:text-[hsl(220,12%,55%)] group-hover:text-slate-900 dark:group-hover:text-white transition-colors">
                      {String(count).padStart(2, '0')}
                    </div>
                  </div>
                  <div className="mt-2.5 h-0.5 w-full bg-slate-100 dark:bg-white/[0.04] overflow-hidden rounded-full">
                    <div className="h-full bg-gradient-to-r from-[hsl(226,100%,55%)] to-[hsl(150,80%,45%)]" style={{ width: Math.min(100, (count / ((stats?.state_counts?.[0]?.[1]) || 1)) * 100) + '%' }} />
                  </div>
                </Link>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* ════════════════  CTA  ════════════════ */}
      <section className="relative border-t border-slate-200 dark:border-white/[0.06] py-20 lg:py-28 overflow-hidden" data-testid="cta-section">
        <div className="hidden dark:block"><AuroraBackground intensity={0.7} /></div>
        <div className="relative max-w-[1280px] mx-auto px-5 sm:px-8 text-center">
          <div className="font-mono text-[11px] tracking-[0.22em] uppercase text-[hsl(150,80%,40%)] dark:text-[hsl(150,80%,60%)] mb-4">◢ FOUNDING SEATS</div>
          <h2 className="font-display font-bold tracking-[-0.04em] leading-[0.95] max-w-4xl mx-auto" style={{ fontSize: 'clamp(2rem, 4.4vw, 3.6rem)' }}>
            Get listed. <span className="italic font-normal text-slate-500 dark:text-[hsl(220,12%,60%)]" style={serifStyle}>Get found.</span>{' '}
            <span className="bg-clip-text text-transparent" style={{ backgroundImage: 'linear-gradient(90deg, hsl(226,100%,52%), hsl(270,95%,60%), hsl(320,90%,55%))' }}>
              Get paid.
            </span>
          </h2>
          <p className="mt-5 max-w-2xl mx-auto text-base text-slate-600 dark:text-[hsl(220,12%,70%)] leading-relaxed">
            The first <span className="font-mono font-bold text-slate-900 dark:text-white">100</span> servers get founding pricing locked in for life — <span className="font-mono font-bold text-slate-900 dark:text-white">$13.99</span>/mo or <span className="font-mono font-bold text-slate-900 dark:text-white">$139</span>/yr — unlimited coverage, statewide visibility, the full toolkit.
          </p>
          <div className="mt-8 flex flex-col sm:flex-row gap-3 justify-center">
            <Magnetic strength={14}>
              <Link to="/register" className="inline-flex items-center justify-center gap-2 px-7 h-12 rounded-xl bg-[hsl(226,100%,55%)] hover:bg-[hsl(226,100%,62%)] text-white font-semibold tracking-tight text-sm shadow-[0_12px_36px_-10px_hsl(226,100%,55%)] transition-colors" data-testid="cta-join">
                Claim Founding Seat <ArrowRight className="h-4 w-4" />
              </Link>
            </Magnetic>
            <Link to="/search" className="inline-flex items-center justify-center gap-2 px-7 h-12 rounded-xl border border-slate-300 dark:border-white/15 hover:border-slate-400 dark:hover:border-white/30 bg-white dark:bg-white/5 text-slate-900 dark:text-white font-semibold tracking-tight text-sm transition-colors" data-testid="cta-search">
              Browse Servers
            </Link>
          </div>
          <div className="mt-8 inline-flex items-center gap-3 text-sm text-slate-500 dark:text-[hsl(220,12%,60%)]">
            <span className="font-mono font-bold tabular-nums text-[hsl(150,80%,40%)] dark:text-[hsl(150,80%,60%)] text-base">
              <AnimatedCounter to={foundingData.remaining} duration={1.4} />
            </span>
            <span>of 100 seats remaining · Locked for life</span>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}

export default HomePage;
